import { useEffect, useState } from "react";
import { DEFAULT_LOCALE } from "@diality/contracts";
import { apiUrl, resolveApiEndpoint } from "./api-endpoint";

/**
 * Site-wide locale support (public side).
 *
 * `useSupportedLocales` reads the supported-locale list from site settings so
 * the language switcher offers exactly what managers have configured. The
 * default-locale fallback renders immediately — the switcher is never blocked
 * on the settings request (the same resilience stance the footer's settings
 * read takes in the layout loader).
 *
 * The visitor's choice is a preference (localStorage `diality-locale`), not a
 * route: pages fall back to the default locale until composed in the chosen
 * one, so the URL stays canonical.
 */

const DEFAULT_LOCALES = [DEFAULT_LOCALE];
const PREFERENCE_KEY = "diality-locale";

interface PublicSiteSettings {
  supportedLocales?: unknown;
}

/** The locales the site offers visitors (default locale until settings load). */
export function useSupportedLocales(): string[] {
  const [locales, setLocales] = useState<string[]>(DEFAULT_LOCALES);

  useEffect(() => {
    let active = true;

    async function loadLocales() {
      try {
        const endpoint = await resolveApiEndpoint();
        const response = await fetch(apiUrl(endpoint, "/api/settings"));
        if (!response.ok) return;

        const settings = (await response.json()) as PublicSiteSettings;
        if (!active || !Array.isArray(settings.supportedLocales)) return;

        const list = settings.supportedLocales.filter(
          (l): l is string => typeof l === "string" && l.length > 0,
        );
        if (list.length > 0) setLocales(list);
      } catch {
        // The default-locale list is retained until settings can be read.
      }
    }

    void loadLocales();
    return () => {
      active = false;
    };
  }, []);

  return locales;
}

/** The visitor's stored language preference, if any. */
export function readLocalePreference(): string | null {
  try {
    return localStorage.getItem(PREFERENCE_KEY);
  } catch {
    return null;
  }
}

/** Persist the visitor's language choice (survives return visits). */
export function writeLocalePreference(locale: string): void {
  try {
    localStorage.setItem(PREFERENCE_KEY, locale);
  } catch {
    // Private mode / storage disabled — the choice simply doesn't persist.
  }
}
